import { useState, cloneElement, useContext, forwardRef } from "react";
import { TbChevronDown } from "react-icons/tb";
import { RiCouponLine } from "react-icons/ri";
import { Link, useLocation } from "react-router-dom";
import { LuComputer, LuFileClock } from "react-icons/lu";
import {
  LuPackageOpen,
  LuStar,
  LuUsers,
  LuShield,
  LuLayoutDashboard,
  LuTags,
  LuSettings2,
  LuCpu,
  LuChartBar,
  LuUndo2,
  LuChevronsLeft,
  LuChevronsRight,
  LuSearch,
} from "react-icons/lu";
import { MdStorefront } from "react-icons/md";
import { Image, Plug } from "lucide-react";
import { MainContext } from "../../AppContext";
import {
  hasAnyPermission,
  hasPermission,
  PERMISSIONS,
  type PermissionFlag,
} from "../../constants/permissions";
import { ASSISTANT_ACCESS_PERMISSIONS } from "../../lib/assistant-access";
import { focusPageSearch } from "../../hooks/useFocusSearchShortcut";
import { openCommandMenu } from "../CommandMenu";
import { LuCommand } from "react-icons/lu";

interface SideBarProps {
  isSidebarOpen: boolean;
  isCollapsed: boolean;
  onToggleCollapsed: () => void;
}

const SideBar = forwardRef<HTMLDivElement, SideBarProps>(function SideBar(
  { isSidebarOpen, isCollapsed, onToggleCollapsed },
  ref,
) {
  const { user, userLoaded } = useContext(MainContext) as {
    user: any;
    userLoaded: boolean;
  };

  const widthClass = isCollapsed ? "sm:w-20" : "sm:w-72";

  // Wait until permissions are fetched; show skeleton sidebar meanwhile
  if (!userLoaded) {
    return (
      <aside
        id="logo-sidebar"
        ref={ref}
        className={`fixed top-0 left-0 z-40 w-72 ${widthClass} h-screen transition-[width,transform] duration-200
        bg-card text-card-foreground border-r border-border
        ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"}
        sm:translate-x-0`}
        aria-label="Sidebar"
      >
        <div className="h-16 border-b border-border flex items-center px-4">
          <div className="h-6 w-24 rounded bg-muted animate-pulse" />
        </div>
        <div className="px-3 pt-4 space-y-2 animate-pulse">
          {Array.from({ length: 8 }).map((_, idx) => (
            <div key={idx} className="h-8 bg-muted/50 rounded-md" />
          ))}
        </div>
      </aside>
    );
  }

  const canAccess = (permission?: number, anyOfPermissions?: readonly PermissionFlag[]) => {
    if (anyOfPermissions) return hasAnyPermission(user?.permissions, anyOfPermissions);
    if (!permission) return true;
    return hasPermission(user?.permissions, permission);
  };

  // Sidebar definitions, in display order. Home/Dashboard first per request.
  const sidebarStructure = [
    {
      title: "Dashboard",
      subItems: [
        { title: "Home", to: "/", icon: <LuLayoutDashboard /> },
        { title: "Analytics", to: "/stats", icon: <LuChartBar /> },
      ],
    },
    {
      title: "Sales & Customers",
      subItems: [
        {
          title: "Orders",
          to: "/orders",
          icon: <LuPackageOpen />,
          permission: PERMISSIONS.ORDERS_VIEW,
        },
        {
          title: "Maintenance",
          to: "/maintenance",
          icon: <LuUndo2 />,
          permission: PERMISSIONS.ORDERS_VIEW,
        },
        {
          title: "Product Reviews",
          to: "/product-reviews",
          icon: <LuStar />,
          permission: PERMISSIONS.ORDERS_VIEW,
        },
        {
          title: "Order Reviews",
          to: "/order-reviews",
          icon: <LuStar />,
          permission: PERMISSIONS.ORDERS_VIEW,
        },
      ],
    },
    {
      title: "Catalog",
      subItems: [
        {
          title: "Products",
          to: "/products",
          icon: <LuCpu />,
          permission: PERMISSIONS.PRODUCTS_VIEW,
        },
        {
          title: "Categories",
          to: "/categories",
          icon: <LuTags />,
          permission: PERMISSIONS.PRODUCTS_VIEW,
        },
        {
          title: "Attributes",
          to: "/attributes",
          icon: <LuSettings2 />,
          permission: PERMISSIONS.PRODUCTS_VIEW,
        },
        {
          title: "Upgrade Configurations",
          to: "/types",
          icon: <LuSettings2 />,
          permission: PERMISSIONS.PRODUCTS_EDIT,
        },
        {
          title: "Upgrade Templates",
          to: "/upgrade-templates",
          icon: <LuComputer />,
          permission: PERMISSIONS.PRODUCTS_EDIT,
        },
        {
          title: "Banners",
          to: "/banners",
          icon: <Image />,
          permission: PERMISSIONS.PRODUCTS_EDIT,
        },
      ],
    },
    {
      title: "Business",
      subItems: [
        {
          title: "Brands",
          to: "/brands",
          icon: <MdStorefront />,
          permission: PERMISSIONS.PRODUCTS_VIEW,
        },
        {
          title: "Coupons",
          to: "/coupons",
          icon: <RiCouponLine />,
          permission: PERMISSIONS.COUPONS_VIEW,
        },
      ],
    },
    {
      title: "Access Control",
      subItems: [
        {
          title: "Users",
          to: "/users",
          icon: <LuUsers />,
          permission: PERMISSIONS.USERS_VIEW,
        },
        {
          title: "Roles",
          to: "/roles",
          icon: <LuShield />,
          permission: PERMISSIONS.USERS_EDIT,
        },
        {
          title: "Audit-Log",
          to: "/auditLogs",
          icon: <LuFileClock />,
          permission: PERMISSIONS.USERS_VIEW,
        },
      ],
    },
    {
      title: "Tools",
      subItems: [
        { title: "PC Generator", to: "/PCGen", icon: <LuComputer /> },
        {
          title: "MCP access",
          to: "/settings/mcp",
          icon: <Plug />,
          anyOfPermissions: ASSISTANT_ACCESS_PERMISSIONS,
        },
      ],
    },
  ];

  const filteredStructure = sidebarStructure
    .map((section) => ({
      ...section,
      subItems: section.subItems.filter((si: any) =>
        canAccess(si.permission, si.anyOfPermissions),
      ),
    }))
    .filter((section) => section.subItems.length > 0);

  return (
    <aside
      id="logo-sidebar"
      ref={ref}
      className={`fixed top-0 left-0 z-40 w-72 ${widthClass} h-screen flex flex-col transition-[width,transform] duration-200
        bg-card text-card-foreground border-r border-border
        ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"}
        sm:translate-x-0`}
      aria-label="Sidebar"
    >
      {/* Brand block */}
      <Link
        to="/"
        className="h-16 flex items-center gap-2.5 px-4 border-b border-border flex-shrink-0 hover:bg-accent transition-colors"
        title="Home"
      >
        <div className="flex-shrink-0 w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-xs">
          R
        </div>
        {!isCollapsed && (
          <span className="text-[15px] font-semibold tracking-tight text-foreground">
            Raiq
          </span>
        )}
        {!isCollapsed && (
          <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-medium text-muted-foreground">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 dark:bg-emerald-400" />
            Live
          </span>
        )}
      </Link>

      {/* Search shortcut hint */}
      {!isCollapsed && (
        <button
          type="button"
          onClick={focusPageSearch}
          className="mx-3 mt-3 flex items-center gap-2 h-9 px-3 rounded-md border border-input bg-background text-[13px] text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
        >
          <LuSearch className="h-3.5 w-3.5" />
          <span className="flex-1 text-left">Search</span>
          <kbd className="inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded border border-border bg-muted text-[11px] font-mono text-muted-foreground">
            /
          </kbd>
        </button>
      )}

      {/* Command palette hint */}
      {!isCollapsed && (
        <button
          type="button"
          onClick={openCommandMenu}
          className="mx-3 mt-2 flex items-center gap-2 h-9 px-3 rounded-md border border-input bg-background text-[13px] text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
        >
          <LuCommand className="h-3.5 w-3.5" />
          <span className="flex-1 text-left">Command palette</span>
          <kbd className="inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded border border-border bg-muted text-[11px] font-mono text-muted-foreground">
            ⌘K
          </kbd>
        </button>
      )}

      {/* Scrollable nav */}
      <div className="flex-1 overflow-y-auto px-3 pt-4 pb-4">
        <ul className="space-y-1 font-medium">
          {filteredStructure.map((section) => (
            <CollapsibleSidebarItem
              key={section.title}
              title={section.title}
              subItems={section.subItems}
              isCollapsed={isCollapsed}
            />
          ))}
        </ul>
      </div>

      {/* Footer — version chip + collapse toggle */}
      <div className="flex-shrink-0 border-t border-border px-3 py-2.5 flex items-center justify-between">
        {!isCollapsed && (
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">
            v2026.04
          </span>
        )}
        <button
          type="button"
          onClick={onToggleCollapsed}
          className={`hidden sm:inline-flex items-center justify-center h-7 w-7 rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors ${isCollapsed ? "mx-auto" : ""}`}
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {isCollapsed ? (
            <LuChevronsRight className="h-4 w-4" />
          ) : (
            <LuChevronsLeft className="h-4 w-4" />
          )}
        </button>
      </div>
    </aside>
  );
});

export default SideBar;

function SidebarItem({
  title,
  icon,
  to,
  isCollapsed,
}: {
  title: string;
  icon: any;
  to: string;
  isCollapsed: boolean;
}) {
  const location = useLocation();
  const isActive =
    to === "/" ? location.pathname === to : location.pathname.startsWith(to);

  const clonedIcon = cloneElement(icon, {
    className: `${icon.props.className || ""} w-4 h-4`.trim(),
    style: { ...icon.props.style, width: "auto", height: "auto" },
  });

  if (clonedIcon.props && clonedIcon.props.style) {
    delete clonedIcon.props.style.width;
    delete clonedIcon.props.style.height;
  }

  return (
    <li>
      <Link
        to={to}
        title={isCollapsed ? title : undefined}
        className={`flex items-center ${isCollapsed ? "justify-center" : ""} h-8 px-2.5 rounded-md text-[13px] transition-colors duration-100 group ${
          isActive
            ? "bg-accent text-accent-foreground font-medium"
            : "text-muted-foreground hover:text-foreground hover:bg-accent/60"
        }`}
      >
        <div
          className={`flex-shrink-0 w-4 h-4 ${
            isActive
              ? "text-foreground"
              : "text-muted-foreground group-hover:text-foreground"
          }`}
        >
          {clonedIcon}
        </div>
        {!isCollapsed && (
          <span className="flex-1 ms-2.5 whitespace-nowrap truncate">
            {title}
          </span>
        )}
      </Link>
    </li>
  );
}

interface SubItem {
  title: string;
  to: string;
  icon: any;
  permission?: number;
  anyOfPermissions?: readonly PermissionFlag[];
}

function CollapsibleSidebarItem({
  title,
  subItems,
  isCollapsed,
}: {
  title: string;
  subItems: SubItem[];
  isCollapsed: boolean;
}) {
  const [isOpen, setIsOpen] = useState(true);
  // In collapsed mode: a thin neutral divider between sections.
  if (isCollapsed) {
    return (
      <li className="mt-4 first:mt-0">
        <div className="flex justify-center pb-2">
          <div className="h-px w-6 bg-border" />
        </div>
        <ul className="space-y-0.5">
          {subItems.map((subItem) => (
            <SidebarItem
              key={subItem.to}
              title={subItem.title}
              icon={subItem.icon}
              to={subItem.to}
              isCollapsed
            />
          ))}
        </ul>
      </li>
    );
  }

  return (
    <li className="mt-5 first:mt-0">
      <button
        className="flex items-center justify-between w-full px-2 py-1 rounded group text-muted-foreground hover:text-foreground transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="text-[11px] uppercase font-medium tracking-[0.1em]">
          {title}
        </span>
        <span
          className={`text-muted-foreground/60 group-hover:text-muted-foreground transition-transform ${isOpen ? "" : "-rotate-90"}`}
        >
          <TbChevronDown size={11} />
        </span>
      </button>
      {isOpen && (
        <ul className="space-y-0.5 mt-1">
          {subItems.map((subItem: SubItem) => (
            <SidebarItem
              key={subItem.to}
              title={subItem.title}
              icon={subItem.icon}
              to={subItem.to}
              isCollapsed={false}
            />
          ))}
        </ul>
      )}
    </li>
  );
}
